<template>
  <div class="notification" :class="{ __read: isRead }" @click="handleClick" :style="{
    pointerEvents: isLoading
      ? 'none' : 'auto', cursor: isNewUserType ? 'default' : 'pointer'
  }">
    <div v-if="!isRead" class="notification__close" @click.stop="readNotification(notification.id)"></div>
    <div class="notification__type" :class="'notification__type--' + getType()"></div>
    <div class="user">
      <div class="user__avatar"></div>
      <div class="user__name">

      </div>
    </div>
    <div class="notification__text">
      {{ notification.data.message }}
    </div>

    <div class="deadline" v-if="getType() == 'pendency'">
      <div class="notification-date">
        <div class="icon icon__date"></div>
        <p>Deadline: {{ deadline }}</p>
      </div>
    </div>


    <div class="notification-date-time ">
      <div class="notification-date">
        <div class="icon icon__date"></div>
        <p>{{ date }}</p>
      </div>

      <div class="notification-time">
        <div class="icon icon__time"></div>
        <p>{{ time }}</p>
      </div>
    </div>

  </div>

</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import moment from "moment";

defineOptions({
  name: "Notification"
});

const props = defineProps({
  notification: {
    type: Object,
    required: true,
  }
});

const store = useStore();
const router = useRouter();

// Local Reactive State Data
const isRead = ref(props.notification.read_at !== null);
const isLoading = ref(false);

// Computed Time Parsing Properties
const date = computed(() => {
  return moment(props.notification.created_at).format("DD.MM.YYYY");
});

const time = computed(() => {
  return moment(props.notification.created_at).format("HH:mm");
});

const deadline = computed(() => {
  if (props.notification?.data?.deadline) {
    return moment(props.notification.data.deadline).format("DD.MM.YYYY HH:mm");
  }
  return "";
});

const isNewUserType = computed(() => {
  const typeValue = getType();
  return typeValue === "new_user" || typeValue === "user_deactivated";
});

// Structural Interactor Actions
const getType = () => {
  if (!props.notification?.data?.type) return "";
  return props.notification.data.type.toLowerCase();
};

const readNotification = async (id) => {
  try {
    isLoading.value = true;
    await store.dispatch("markNotificationAsRead", id);
    isRead.value = true;
    await store.dispatch("getUnreadNotificationsCount");
  } catch (error) {
    console.error("Failed to mark notification as read:", error);
  } finally {
    isLoading.value = false;
  }
};

const handleClick = async () => {
  if (isNewUserType.value) return;

  if (!isRead.value) {
    await readNotification(props.notification.id);
  }

  const data = props.notification.data;

  if (data.entity_type && data.entity_id) {
    let entity = data.entity_type.toLowerCase();
    let tab = 0;

    if (entity === "customer") {
      entity = "private";
      tab = 1;
    } else if (entity === "lead") {
      tab = 10;
    } else if (entity === "offer") {
      tab = 2;
    } else if (entity === "pendency") {
      tab = 5;
    } else if (entity === "note") {
      tab = 4;
    } else if (entity === "call") {
      tab = 3;
    }

    router.push({
      name: "AccountDetails",
      params: {
        entity: entity,
        id: data.entity_id,
      },
      query: {
        tab: tab,
      },
    });
  }
};
</script>

<style lang="scss" scoped>
.notification {
  padding: 50px 0px 60px 20px;
  background: var(--bg-16);
  border-radius: var(--radius-1);
  position: relative;
  margin-bottom: 30px;
  pointer-events: auto;

  /* Ensure it's interactive */
  &.__loading {
    pointer-events: none;
    /* Disable during loading */
    opacity: 0.5;
    /* Visual feedback (optional) */
  }

  &.__read {
    background: #fff;
    border: 1px solid #F2F4F6;
  }

  &:last-child {
    margin-bottom: 0;

  }

  &:hover {
    box-shadow: 0px 10px 15px #0000001A;
    cursor: pointer;
  }

  .user {
    top: -27px;
    left: 50%;
    transform: translateX(-50%);

    &__avatar {
      height: 46px;
      width: 46px;
      background: black;
      margin: 3px auto;
      border: 2px solid var(--border-16);
    }

  }

  &__close {
    top: -8px;
    right: -8px;
    height: 24px;
    width: 24px;
    background: url("../assets/icons/default/close_hover_icon.svg");
    cursor: pointer;
  }

  &__type {
    height: 34px;
    width: 34px;
    background-color: var(--bg-1);
    box-shadow: 0px 8px 10px var(--shadow-6);
    left: 15px;
    top: -17px;

    &--customer {
      background-image: url("../assets/icons/default/users_icon.svg");
    }

    &--contact {
      background-image: url("../assets/icons/default/contact_icon_left_menu.svg");
    }

    &--potential {
      background-image: url("../assets/icons/default/potentialcustomer_icon.svg");
    }

    &--user {
      background-image: url("../assets/icons/default/customers_icon.svg");
    }

    &--birthday {
      background-image: url("../assets/icons/default/cake_icon.svg");
    }

    &--pendency {
      background-image: url("../assets/icons/default/modal_pendencies_icon_colored.svg");
    }

    &--customer,
    &--contact,
    &--potential,
    &--user,
    &--birthday,
    &--pendency {
      background-repeat: no-repeat;
      background-position: center;
      background-size: 24px;
    }

  }

  &__text {
    color: var(--text-9);
    line-height: 18px;
  }

  &__date {
    right: 13px;
    bottom: 6px;
    font-size: var(--x-smaller);
  }

  &__type,
  .user__avatar {
    border-radius: 50%
  }

  .user__avatar {
    background-image: url("../assets/icons/default/notification_icon_new.svg");
    background-repeat: no-repeat;
    background-position: center;
    background-color: #22DB8E;
  }

  &__date,
  .user__name {
    color: var(--text-2);
    font-family: var(--muli-text-semi-bold);
  }

  .user__name,
  &__text {
    font-size: var(--x-small);
  }

  .user,
  &__date,
  &__close,
  &__type {
    position: absolute;
  }
}

.deadline {
  position: absolute;
  bottom: 10px;
  left: 14px;
}

.notification-date-time {
  display: flex;
  justify-content: space-between;
  position: absolute;
  right: 14px;
  bottom: 10px;
  gap: 14px;
}

.notification-date,
.notification-time {
  padding: 3px 13px;
  padding-left: 6px;
  padding-bottom: 1px;
  box-shadow: 0px 8px 10px #00000026;
  border-radius: 10px;
  display: flex;
  align-items: center;
  background-color: #FFFFFF;
}

.notification-date .icon,
.notification-time .icon {
  transform: scale(0.7);
  margin-bottom: 2px;
}

.notification-date p,
.notification-time p {
  color: var(--text-2);
  font-family: var(--muli-text-semi-bold);
  font-size: var(--x-smaller);
}

[data-theme="dark"] {

  .notification-date,
  .notification-time {
    background-color: #313B47;
  }

  .notification {

    &.__read {
      background: var(--bg-1);
    }
  }
}
</style>
